import React, { useCallback, useState } from 'react';
import { blogRunsService } from '../../../../services/blogRunsService';
import { jobsService } from '../../../../services/jobsService';
import { useArticleContext } from '../../ArticleContext';
import { STAGE_ORDER } from './articleTypes';

function formatCost(cost: number): string {
  if (cost === 0) return '$0.00';
  if (cost < 0.01) return '<$0.01';
  return `$${cost.toFixed(2)}`;
}

function TotalCostBadge() {
  const { run } = useArticleContext();
  const total = (run?.stages || []).reduce((sum, s: any) => {
    const c = s?.total_cost_usd ? Number(s.total_cost_usd) : 0;
    return sum + (Number.isFinite(c) ? c : 0);
  }, 0);
  if (total === 0) return null;
  const totalReruns = (run?.stages || []).reduce((sum, s: any) => sum + Math.max(0, ((s?.runs?.length) || 0) - 1), 0);
  return (
    <span
      className="text-xs font-mono text-gray-500 px-2 py-0.5 rounded-md border border-gray-200 bg-gray-50"
      title={totalReruns > 0 ? `${formatCost(total)} total · ${totalReruns} rerun${totalReruns !== 1 ? 's' : ''}` : `${formatCost(total)} total`}
    >
      {formatCost(total)}
      {totalReruns > 0 && <span className="text-amber-500 ml-1">↻{totalReruns}</span>}
    </span>
  );
}

export function RunControls() {
  const { run, refreshRun, refreshDocument, refreshArticles } = useArticleContext();
  const [pipelineRunning, setPipelineRunning] = useState(false);

  const handleRunPipeline = useCallback(async () => {
    if (!run?.run_id) return;

    const completedStageIds = new Set(
      (run.stages || [])
        .filter((s: { status: string }) => s.status === 'succeeded')
        .map((s: { stage_id: string }) => s.stage_id)
    );
    const fromStage = STAGE_ORDER.find(s => !completedStageIds.has(s));
    if (!fromStage) return;

    setPipelineRunning(true);
    try {
      await blogRunsService.update(run.run_id, {
        skip_stages: ['stage_8_wp_publish'],
        status: 'running',
      } as any);

      const result = await blogRunsService.executePipeline(run.run_id, {
        mode: 'headless',
        from_stage_id: fromStage,
      });

      if (result.job_id) {
        const pollInterval = 5000;
        const maxPolls = 120;
        for (let i = 0; i < maxPolls; i++) {
          await new Promise(r => setTimeout(r, pollInterval));
          try {
            const job = await jobsService.get(result.job_id);
            await refreshRun();
            await refreshDocument();
            if (job.status === 'succeeded' || job.status === 'failed' || job.status === 'cancelled') {
              await refreshArticles();
              break;
            }
          } catch {
            break;
          }
        }
      }
    } catch (e: any) {
      console.error('Pipeline start failed:', e);
    } finally {
      setPipelineRunning(false);
    }
  }, [run, refreshRun, refreshDocument, refreshArticles]);

  const isRunning = run?.status === 'running';
  const isPaused = run?.status === 'paused';
  const isComplete = run?.status === 'succeeded';
  const hasCompletedSetup = (run?.stages || []).some(
    (s: { stage_id: string; status: string }) => s.stage_id === 'stage_2a_serp_overview' && s.status === 'succeeded'
  );

  if (isComplete) {
    return (
      <div className="flex items-center gap-2">
        <TotalCostBadge />
        <span className="text-xs text-green-600 font-medium">Complete</span>
      </div>
    );
  }
  if (isRunning || pipelineRunning) {
    return (
      <div className="flex items-center gap-2">
        <TotalCostBadge />
        <span className="text-xs text-blue-600 font-medium flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
          Running...
        </span>
        <button
          type="button"
          className="px-2 py-0.5 text-xs rounded border border-red-300 text-red-600 hover:bg-red-50"
          onClick={async () => {
            if (!run?.run_id || !run.pipeline_job_id) return;
            try {
              await jobsService.cancel(run.pipeline_job_id);
              await blogRunsService.update(run.run_id, { status: 'paused' } as any);
              await refreshRun();
              await refreshArticles();
            } catch (e: any) {
              console.error('Failed to stop pipeline:', e);
            }
          }}
        >
          Stop
        </button>
      </div>
    );
  }
  if (isPaused) {
    const lastError = (run as any)?.last_error;
    const pausedAt = (run as any)?.paused_at_stage;
    return (
      <div className="flex flex-col items-end gap-1.5">
        <div className="flex items-center gap-2">
          <TotalCostBadge />
          {pausedAt && (
            <span className="text-xs text-amber-600">
              Paused at {pausedAt.replace('stage_', '').replace(/_/g, ' ')}
            </span>
          )}
          <button type="button" className="px-3 py-1 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700" onClick={handleRunPipeline}>
            Resume
          </button>
        </div>
        {lastError && (
          <div className="text-xs text-red-600 max-w-md text-right truncate" title={lastError}>
            {lastError.length > 80 ? lastError.slice(0, 80) + '...' : lastError}
          </div>
        )}
      </div>
    );
  }
  if (hasCompletedSetup) {
    return (
      <div className="flex items-center gap-2">
        <TotalCostBadge />
        <button type="button" className="px-3 py-1 text-xs font-medium rounded-md text-white hover:opacity-90" style={{ backgroundColor: '#1a2332' }} onClick={handleRunPipeline}>
          Generate Article
        </button>
      </div>
    );
  }
  return <TotalCostBadge />;
}
